import { ParagraphViewMode } from "../../components/rule-details/ParagraphView";
import { PAGES } from "../../context/PageContext";
import { cacheManager } from "../../utils/cacheManager";
import { applyCommentChange, getBookmarkedSectionsHtml, getBookmarksBySelectedText, getExpandedRangeForBookmarks, withTemporaryTrackingMode, withWordContextWrite } from "../../utils/document";
import { RECOVERY_USED_MESSAGE } from "../../utils/ooxml-recovery";
import { appliedCommentReportOf, tellAgentsCardsApplied } from "../../utils/redline-applied-cards";
import { makeRuleDocumentOrderComparator } from "../../utils/rules";
import { toastDismiss, toastError, toastLoading } from "../../utils/toast";
import { Searcher } from "fast-fuzzy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { getOriginalActionIndex, getOriginalStepIndex, getValidCommentActionsAndSteps, getValidRedlineActionsAndSteps, hasCommentStepContent, hasRedlineStepContent } from "./rule-action-plan";
import type { ActionTrackedChange } from "./rule-action-plan";
import type { ApplyRedlineArgs } from "@hooks/use-apply-redline";
import type { ErrorContext } from "src/context/AnalyticsContext";
import type { Playbook } from "src/utils/web-api";

export interface UseRuleSelectionInputs {
  reviewReport: any[];
  ruleSectionOrder: Record<string, number>;
  selectedRuleUuid: string;
  removeRespondedRule: (ruleId: string) => Promise<void>;
  setSelectedRuleUuid: (uuid: string | null) => void;
  showPage: (pageName: string, params?: Record<string, any>) => void;
  setIsConceding: React.Dispatch<React.SetStateAction<boolean>>;
  removeConcededRule: (ruleId: string) => Promise<void>;
  addConcededRule: (ruleId: string) => Promise<void>;
  setIsParagraphsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setParagraphs: React.Dispatch<React.SetStateAction<any[]>>;
  playbooks: Playbook[];
  addRespondedRule: (ruleId: string) => Promise<void>;
  sendError: (error: unknown, context?: ErrorContext | Record<string, any>) => void;
  getRuleTrackedChanges: (selectedRule: any) => ActionTrackedChange[];
  getRuleBookmarkNames: (selectedRule: any) => string[];
  concededRules: string[];
  setIsApplyingAll: React.Dispatch<React.SetStateAction<boolean>>;
  applyRedline: (args: ApplyRedlineArgs) => Promise<boolean>;
  updateActionRecoveryWarning: (ruleUuid: string, actionIndex: number, stepIndex: number, warning: string | null) => void;
  updateActionApplied: (ruleUuid: string, actionIndex: number, stepIndex: number, applied: boolean) => void;
  selectedTabOption: string;
  respondedRules: string[];
}

export function useRuleSelection({ reviewReport, ruleSectionOrder, selectedRuleUuid, removeRespondedRule, setSelectedRuleUuid, showPage, setIsConceding, removeConcededRule, addConcededRule, setIsParagraphsLoading, setParagraphs, playbooks, addRespondedRule, sendError, getRuleTrackedChanges, getRuleBookmarkNames, concededRules, setIsApplyingAll, applyRedline, updateActionRecoveryWarning, updateActionApplied, selectedTabOption, respondedRules }: UseRuleSelectionInputs) {
    const [otherRuleIndicesFromSelection, setOtherRuleIndicesFromSelection] = useState<string[]>([]);
    const [bookmark, setBookmark] = useState<string | null>(null);
    const [isBookmarkAnalysisLoading, setIsBookmarkAnalysisLoading] = useState(false);
    const [isDocumentReviewed, setIsDocumentReviewed] = useState(false);
    const ruleSectionOrderKey = JSON.stringify(ruleSectionOrder);
    const doneRules = useMemo(() => {
        const isFirstRuleWithUuid = (rule: any, ruleIndex: number, rules: any[]) =>
            rules.findIndex(other => other.rule.uuid === rule.rule.uuid) === ruleIndex;
        const sectionOrderByRuleUuid: Record<string, number> = JSON.parse(ruleSectionOrderKey);
        const hasStatus = reviewReport?.some(item => item.rule.status);
        let rules = reviewReport;
        if (hasStatus) {
            rules = reviewReport?.filter(item => {
                const hasValidStatus = item.rule.status === "DONE" ||
                    item.rule.status === "SKIPPED" ||
                    item.rule.status === "REMEDY" ||
                    item.rule.status === "DETECTION";

                const hasActionPlanWithContent = item.action_plan && item.action_plan.length > 0;
                const hasBeenApplied = item.match === false && hasActionPlanWithContent;

                return hasValidStatus || hasBeenApplied;
            });
        }

        return rules
            ?.filter(isFirstRuleWithUuid)
            .slice()
            .sort(makeRuleDocumentOrderComparator(
                sectionOrderByRuleUuid,
                r => r.rule.uuid,
                r => r.index ?? Number.MAX_SAFE_INTEGER,
                r => r.rule.name,
            ));
    }, [reviewReport, ruleSectionOrderKey]);
    const selectedRule = useMemo(() => {
        if (!selectedRuleUuid || !doneRules) return null;
        return doneRules.find(rule => rule.rule.uuid === selectedRuleUuid) || null;
    }, [selectedRuleUuid, doneRules]);
    const actionBookmarkNames = selectedRule ? getRuleBookmarkNames(selectedRule) : [];
    const [actionSectionBookmarks, setActionSectionBookmarks] = useState<string[]>(actionBookmarkNames);
    const actionTrackedChanges = selectedRule ? getRuleTrackedChanges(selectedRule) : [];
    const changeDetectionReport = selectedRule?.["change_detection_report"];
    const paragraphViewMode = !!changeDetectionReport ? ParagraphViewMode.FirstParty : ParagraphViewMode.ThirdParty;
    const tabOptionsKey = JSON.stringify(remedyTabOptions(selectedRule));
    const tabOptions = useMemo<string[]>(() => JSON.parse(tabOptionsKey), [tabOptionsKey]);
    const currentActionPlan = selectedRule
        ? selectedRule.alternative_action_plans?.[tabOptions.indexOf(selectedTabOption) - 1] ?? selectedRule.action_plan
        : null;
    const isRuleResponded = selectedRule ? respondedRules.includes(selectedRule.rule.uuid) : false;
    const isRuleConceded = selectedRule ? concededRules.includes(selectedRule.rule.uuid) : false;
    const handleApplyAll = async () => {
        if (!selectedRule?.rule?.uuid) return;
        setIsApplyingAll(true);

        try {
            if (currentActionPlan && currentActionPlan.length > 0) {
                const { validActions: validRedlineActions, validStepsMap: validRedlineStepsMap } = getValidRedlineActionsAndSteps(currentActionPlan);
                for (let actionIndex = 0; actionIndex < validRedlineActions.length; actionIndex++) {
                    const action = validRedlineActions[actionIndex];
                    const validSteps = validRedlineStepsMap.get(action) || [];
                    for (let stepIndex = 0; stepIndex < validSteps.length; stepIndex++) {
                        const step = validSteps[stepIndex];

                        const ooxml = step.action_report.target_ooxml_after?.join('') || '';
                        const textRedline = step.action_report.target_texts ?? null;
                        const bookmarks = step.action_report.target_bookmark_names || [];

                        const stepHasNothingToWrite = !ooxml && textRedline === null;
                        const stepHasNowhereToWrite = bookmarks.length === 0;
                        if (stepHasNothingToWrite || stepHasNowhereToWrite) {
                            continue;
                        }

                        const originalActionIndex = getOriginalActionIndex(actionIndex, validRedlineActions, currentActionPlan);
                        const originalStepIndex = getOriginalStepIndex(stepIndex, validSteps, action);

                        const applied = await applyRedline({
                            ooxml,
                            textRedline,
                            cardComments: [],
                            actionId: null,
                            initiatedFrom: 'single',
                            bookmarks,
                            onStatus: (status) => {
                                if (status === 'warning' && selectedRule?.rule?.uuid) {
                                    updateActionRecoveryWarning(
                                        selectedRule.rule.uuid,
                                        originalActionIndex,
                                        originalStepIndex,
                                        RECOVERY_USED_MESSAGE,
                                    );
                                }
                            },
                        });

                        if (applied) {
                            handleActionApplied(originalActionIndex, originalStepIndex);
                        }
                    }
                }

                const { validActions: validCommentActions, validStepsMap: validCommentStepsMap } = getValidCommentActionsAndSteps(currentActionPlan);
                for (let actionIndex = 0; actionIndex < validCommentActions.length; actionIndex++) {
                    const action = validCommentActions[actionIndex];
                    const validSteps = validCommentStepsMap.get(action) || [];
                    for (let stepIndex = 0; stepIndex < validSteps.length; stepIndex++) {
                        const step = validSteps[stepIndex];
                        const comments = step.action_report.action_tracked_changes?.filter((change: any) => change.tag === 'comment') || [];
                        const bookmarks = step.action_report.target_bookmark_names || [];

                        let anyCommentApplied = false;
                        for (const comment of comments) {
                            if (bookmarks.length > 0) {
                                await withWordContextWrite(async (context) => {
                                    const range = await getExpandedRangeForBookmarks(context, bookmarks);

                                    if (range) {
                                        await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
                                            await applyCommentChange(range, comment.text);
                                        });
                                        tellAgentsCardsApplied([appliedCommentReportOf({ cardId: null, paragraphBookmarkNames: bookmarks, text: comment.text, quotedText: null })]);
                                        anyCommentApplied = true;
                                    }
                                });
                            }
                        }

                        if (anyCommentApplied) {
                            const originalActionIndex = getOriginalActionIndex(actionIndex, validCommentActions, currentActionPlan);
                            const originalStepIndex = getOriginalStepIndex(stepIndex, validSteps, action);
                            handleActionApplied(originalActionIndex, originalStepIndex);
                        }
                    }
                }
            }

            await addRespondedRule(selectedRule.rule.uuid);
            setSelectedRuleUuid(null);
            showPage(PAGES.documentReviewPage);
        } catch (error) {
            console.error("Failed to apply all changes:", error);
            sendError(error, {
                component: "RuleDetailsPage",
                action: "handleApplyAll",
                ruleUuid: selectedRule?.rule?.uuid
            });
        } finally {
            setIsApplyingAll(false);
        }
    };
    const handleToggleResponded = async () => {
        if (!selectedRule?.rule?.uuid) return;

        try {
            toastLoading("Applying changes...", {duration: Infinity});
            if (isRuleResponded) {
                await removeRespondedRule(selectedRule.rule.uuid);
                setSelectedRuleUuid(null);
                showPage(PAGES.documentReviewPage);
            } else {
                await handleApplyAll();
            }
        } catch (error) {
            console.error("Failed to toggle responded status:", error);
        } finally {
            toastDismiss();
        }
    };
    const handleToggleConceded = async () => {
        if (!selectedRule?.rule?.uuid) return;
        setIsConceding(true);

        try {
            if (isRuleConceded) {
                await removeConcededRule(selectedRule.rule.uuid);
            } else {
                await addConcededRule(selectedRule.rule.uuid);
            }
            setSelectedRuleUuid(null);
            showPage(PAGES.documentReviewPage);
        } catch (error) {
            console.error("Failed to toggle conceded status:", error);
            toastError("Failed to update this issue. Please try again.");
        } finally {
            setIsConceding(false);
        }
    };
    useEffect(() => {
        setOtherRuleIndicesFromSelection(prev => prev.length > 0 ? [] : prev);
        return () => {
            setBookmark(null);
        };
    }, [selectedRuleUuid]);
    useEffect(() => {
        if (bookmark) {
            (async () => {
                setIsBookmarkAnalysisLoading(true);
                try {
                    const currentBookmarks = await getBookmarksBySelectedText();
                    const ruleUuids = Array.from(
                        new Set(
                            currentBookmarks.flatMap((bookmarkName) => {
                                const matchingRuleUuids = reviewReport
                                    .map((rule) =>
                                        rule.detection_report.rule_match && (
                                            (paragraphViewMode === ParagraphViewMode.FirstParty && rule.detection_report.bookmark_name?.includes(bookmarkName)
                                                ||
                                                paragraphViewMode === ParagraphViewMode.ThirdParty &&
                                                rule.action_plan.flatMap((a) => a.action_tracked_changes)
                                                    .some((change) => change && change.bookmark_name === bookmarkName))
                                        ) ? rule.rule.uuid : null
                                    );
                                return matchingRuleUuids.filter((uuid) => uuid !== null && uuid !== selectedRuleUuid);
                            })
                        )
                    );

                    setOtherRuleIndicesFromSelection(ruleUuids);
                } catch (error) {
                    console.error("Error analyzing bookmark:", error);
                } finally {
                    setIsBookmarkAnalysisLoading(false);
                }
            })();
        }
    }, [bookmark, selectedRuleUuid]);
    useEffect(() => {
        if (isDocumentReviewed) {
            const extractParagraphsForBookmarkedSections = async () => {
                setIsParagraphsLoading(true);
                const paragraphsCacheKey = `paragraphs_${actionBookmarkNames.join('_')}`;
                const bookmarkedSectionCacheKey = `bookmarked_sections_${actionBookmarkNames.join('_')}`;
                try {
                    const cachedParagraphs = await cacheManager.getItem<any[]>(paragraphsCacheKey);
                    const cachedBookmarkedSections = await cacheManager.getItem<any[]>(bookmarkedSectionCacheKey);
                    if (cachedParagraphs) {
                        setParagraphs(cachedParagraphs);
                        setActionSectionBookmarks(cachedBookmarkedSections);
                        return;
                    }

                    const paragraphs: any[] = [];
                    const { htmlValues, groupedBookmarks } = await getBookmarkedSectionsHtml(actionBookmarkNames);
                    setActionSectionBookmarks(groupedBookmarks);
                    for (let i = 0; i < htmlValues.length; i++) {
                        const html = htmlValues[i];
                        let paragraph = html;

                        const regex =
                            /<span\s+class=([^"'\s]+)\s*>\s*<(del|ins)\s+cite=["']mailto:([^"']*)["']\s+datetime=["']([^"']*)["']\s*>([\s\S]*?)<\/\2>\s*<\/span>/gi;

                        const decodeHtml = (value: string) => {
                            const textarea = document.createElement("textarea");
                            textarea.innerHTML = value;
                            return textarea.value;
                        };

                        const checkCondition = (tag: string, citeValue: string, dateTime: string, content: string) => {
                            for (const trackedChange of actionTrackedChanges) {
                                if (trackedChange.tag !== tag) continue;
                                if (trackedChange.author !== decodeURI(citeValue)) continue;
                                if (trackedChange.date.replace(/:\d{2}Z$/, "") !== dateTime) continue;
                                const decodedContent = decodeHtml(content.replace(/\r?\n/g, " ").trim());
                                const trimmedTrackedChangeText = trackedChange.text.trim();

                                if (decodedContent.length < 10) {
                                    if (trimmedTrackedChangeText !== decodedContent) continue;
                                } else {
                                    const searcher = new Searcher([trimmedTrackedChangeText], {
                                        threshold: 0.9,
                                        returnMatchData: true
                                    });
                                    const match = searcher.search(decodedContent)[0];
                                    if (!match) continue;
                                }
                                return true;
                            }
                            return false;
                        };

                        paragraph = paragraph.replace(regex, (fullMatch, spanClass, tag, citeValue, dateTime, content) => {
                            const shouldHighlight = checkCondition(tag, citeValue, dateTime, content);

                            if (shouldHighlight) {
                                const updatedHtml = `<${tag} style="background-color: yellow;" cite="mailto:${citeValue}" datetime="${dateTime}">${content}</${tag}>`;
                                return `<span class="${spanClass}">${updatedHtml}</span>`;
                            }

                            return fullMatch;
                        });

                        paragraphs.push(paragraph);
                    }

                    await cacheManager.setItem(paragraphsCacheKey, paragraphs);
                    await cacheManager.setItem(bookmarkedSectionCacheKey, groupedBookmarks);
                    setParagraphs(paragraphs);
                } catch (error) {
                    console.error("Error extracting paragraphs for bookmarked sections:", error);
                } finally {
                    setIsParagraphsLoading(false);
                }
            };

            extractParagraphsForBookmarkedSections();
        }
    }, [bookmark, selectedRuleUuid, isDocumentReviewed]);
    const handleSelectedParagraphChange = (index: number) => {
        if (paragraphViewMode === ParagraphViewMode.ThirdParty) {
            setBookmark(actionSectionBookmarks[index]);
        }
    };
    function getCurrentRuleIndex() {
        if (!selectedRuleUuid || !doneRules) return -1;
        return doneRules.findIndex(rule => rule.rule.uuid === selectedRuleUuid);
    }
    function getPreviousRuleUuid() {
        const currentIndex = getCurrentRuleIndex();
        if (currentIndex > 0 && doneRules) {
            return doneRules[currentIndex - 1].rule.uuid;
        }
        return null;
    }
    function getNextRuleUuid() {
        const currentIndex = getCurrentRuleIndex();
        if (currentIndex >= 0 && doneRules && currentIndex < doneRules.length - 1) {
            return doneRules[currentIndex + 1].rule.uuid;
        }
        return null;
    }
    function handleEditRule() {
        const ruleUuid = selectedRule?.rule.uuid;

        const playbookFromReview = playbooks?.find(pb => pb.bySection?.some(section => section.rules?.some(rule => rule.id === ruleUuid)));
        showPage(PAGES.rulePage, {
            ruleId: selectedRule?.rule.uuid,
            playbookId: playbookFromReview?.id,
            mode: "edit",
            returnPage: PAGES.ruleDetailsPage
        });
    }
    const updateActionAppliedRef = useRef(updateActionApplied);
    updateActionAppliedRef.current = updateActionApplied;
    const handleActionApplied = useCallback((actionIndex: number, stepIndex: number) => {
        if (selectedRule?.rule?.uuid && actionIndex >= 0 && stepIndex >= 0) {
            updateActionAppliedRef.current(selectedRule.rule.uuid, actionIndex, stepIndex, true);
        }
    }, [selectedRule?.rule?.uuid]);
    const addRespondedRuleRef = useRef(addRespondedRule);
    addRespondedRuleRef.current = addRespondedRule;
    const isEveryApplicableStepApplied = everyApplicableStepApplied(selectedRule?.action_plan);
    useEffect(() => {
        if (!selectedRule?.rule?.uuid || isRuleResponded || isRuleConceded || !isEveryApplicableStepApplied) return;
        addRespondedRuleRef.current(selectedRule.rule.uuid).catch((error) => {
            console.error("Failed to auto-mark rule as responded:", error);
        });
    }, [selectedRule?.rule?.uuid, isRuleResponded, isRuleConceded, isEveryApplicableStepApplied]);
  return { selectedRule, otherRuleIndicesFromSelection, bookmark, isBookmarkAnalysisLoading, isDocumentReviewed, handleSelectedParagraphChange, getPreviousRuleUuid, getNextRuleUuid, handleEditRule, handleToggleResponded, handleToggleConceded, setIsDocumentReviewed, changeDetectionReport, paragraphViewMode, isRuleConceded, handleActionApplied, currentActionPlan, tabOptions, isRuleResponded };
}

function remedyTabOptions(selectedRule: any): string[] {
    const alternativeTabOptions = (selectedRule?.alternative_action_plans ?? []).map(
        (altPlan: any, index: number) => altPlan?.[0]?.action?.remedy_name || `Fallback ${index + 1}`,
    );
    return [selectedRule?.action_plan?.[0]?.action?.remedy_name || "Preferred", ...alternativeTabOptions];
}

function everyApplicableStepApplied(actionPlan: any[] | undefined): boolean {
    const applicableSteps = (actionPlan ?? [])
        .flatMap((action: any) => action.steps ?? [])
        .filter((step: any) => hasRedlineStepContent(step) || hasCommentStepContent(step));
    return applicableSteps.length > 0 && applicableSteps.every((step: any) => step.action_applied === true);
}
